import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Camera, ShieldCheck, Lock, 
  Settings, Mail, Key, Bell, User 
} from 'lucide-react';

import { useProfileStore } from '@/core/application/state/useProfileStore';
import { ProfessionalProfileService } from '@/core/application/directory/ProfessionalProfileService';

export function ProfileSettingsScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const store = useProfileStore.getState();
      if (!store.hasChecked) {
        await store.fetchProfile();
      }
      setProfile(store.profile);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (!profile?.nombres_completos) return '';
    return (profile.nombres_completos.charAt(0) + (profile.apellidos_completos?.charAt(0) || '')).toUpperCase();
  };

  const uploadImageFile = async (file: File) => {
    setUploadingImage(true);
    const oldAvatar = profile?.avatar_url;
    const localUri = URL.createObjectURL(file);
    setProfile((prev: any) => ({ ...prev, avatar_url: localUri }));

    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = (e: any) => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64Data = await base64Promise;

      const extension = file.name.split('.').pop() || 'jpg';
      const url = await ProfessionalProfileService.uploadAvatar(base64Data, extension);
      
      setProfile((prev: any) => ({ ...prev, avatar_url: url }));
      await useProfileStore.getState().fetchProfile(true);

    } catch (error) {
      console.error(error);
      globalThis.alert('No se pudo subir la foto a la nube.');
      setProfile((prev: any) => ({ ...prev, avatar_url: oldAvatar }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePickImage = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        uploadImageFile(file);
      }
    };
    fileInput.click();
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
      </div>
    );
  }

  return (
    <div style={styles.mainContainer}>
      <div style={styles.safeArea}>
        {/* Navbar minimalista claro */}
        <div style={styles.navbar}>
          <button onClick={() => navigate(-1)} style={styles.backButton}>
            <ArrowLeft size={24} color="#64748B" />
          </button>
          <h1 style={styles.navbarTitle}>Mi Cuenta</h1>
          <div style={{ width: '24px' }} />
        </div>

        <div style={styles.scrollContent}>
          
          {/* Avatar Section - Clean White */}
          <div style={styles.avatarSection}>
            <div style={styles.avatarContainer}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" style={styles.avatarImage} />
              ) : (
                <div style={styles.avatarPlaceholder}>
                  {profile?.nombres_completos ? (
                    <span style={styles.avatarInitials}>{getInitials()}</span>
                  ) : (
                    <User size={40} color="#94A3B8" />
                  )}
                </div>
              )}
              {uploadingImage && (
                <div style={{...styles.avatarPlaceholder, position: 'absolute', top: 0, left: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10}}>
                  <div style={styles.spinnerSmall} />
                </div>
              )}
              <button 
                style={styles.editAvatarBadge}
                onClick={handlePickImage}
                disabled={uploadingImage}
              >
                <Camera size={16} color="#FFFFFF" />
              </button>
            </div>
            
            <h2 style={styles.userName}>
              {profile?.nombres_completos ? `${profile.nombres_completos} ${profile.apellidos_completos || ''}` : 'Usuario Aura'}
            </h2>
            {profile?.es_profesional_verificado && (
              <div style={styles.roleContainer}>
                <span style={styles.userRole}>Profesional Clínico</span>
              </div>
            )}
          </div>

          {/* Identidad Legal Bloqueada */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <ShieldCheck size={20} color="#059669" />
              <h3 style={styles.sectionTitle}>Datos de Identidad</h3>
            </div>
            <p style={styles.sectionSubtitle}>
              Tu seguridad es nuestra prioridad. Estos datos verifican tu identidad y están protegidos. Si necesitas actualizar algo, nuestro equipo de soporte estará feliz de ayudarte.
            </p>

            <div style={styles.card}>
              <div style={styles.fieldRow}>
                <span style={styles.fieldLabel}>Documento de Identidad</span>
                <div style={styles.fieldValueContainer}>
                  <Lock size={14} color="#94A3B8" style={{ marginRight: '6px' }} />
                  <span style={styles.fieldValue}>{profile?.perfil_dni || 'No registrado'}</span>
                </div>
              </div>
              <div style={styles.divider} />
              <div style={styles.fieldRow}>
                <span style={styles.fieldLabel}>Fecha de Nacimiento</span>
                <div style={styles.fieldValueContainer}>
                  <Lock size={14} color="#94A3B8" style={{ marginRight: '6px' }} />
                  <span style={styles.fieldValue}>{profile?.perfil_fecha_nacimiento || 'No registrado'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Configuración Editable */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Settings size={20} color="#334155" />
              <h3 style={styles.sectionTitle}>Configuración de Cuenta</h3>
            </div>

            <div style={styles.card}>
              <div style={styles.menuRow}>
                <div style={styles.menuIconContainer}>
                  <Mail size={20} color="#3B82F6" />
                </div>
                <div style={styles.menuTextContainer}>
                  <span style={styles.menuText}>Cambiar Correo Electrónico</span>
                  <span style={styles.menuSubText}>{profile?.correo || 'No disponible'}</span>
                </div>
                <div style={styles.badgeSoon}>
                  <span style={styles.badgeSoonText}>Próximamente</span>
                </div>
              </div>
              
              <div style={styles.divider} />
              
              <div style={styles.menuRow}>
                <div style={styles.menuIconContainer}>
                  <Key size={20} color="#8B5CF6" />
                </div>
                <div style={styles.menuTextContainer}>
                  <span style={styles.menuText}>Cambiar Contraseña</span>
                  <span style={styles.menuSubText}>Actualiza tu clave de acceso</span>
                </div>
                <div style={styles.badgeSoon}>
                  <span style={styles.badgeSoonText}>Próximamente</span>
                </div>
              </div>
              
              <div style={styles.divider} />
              
              <div style={styles.menuRow}>
                <div style={styles.menuIconContainer}>
                  <Bell size={20} color="#F59E0B" />
                </div>
                <div style={styles.menuTextContainer}>
                  <span style={styles.menuText}>Preferencias de Notificaciones</span>
                  <span style={styles.menuSubText}>Canales, alertas y recordatorios</span>
                </div>
                <div style={styles.badgeSoon}>
                  <span style={styles.badgeSoonText}>Próximamente</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  mainContainer: { flex: 1, backgroundColor: '#F8FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  safeArea: { flex: 1, display: 'flex', flexDirection: 'column' },
  loadingContainer: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8FAFC', minHeight: '100vh' },
  spinner: { width: '40px', height: '40px', border: '4px solid #0284C7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinnerSmall: { width: '24px', height: '24px', border: '3px solid #0284C7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  
  navbar: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #F1F5F9' },
  backButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navbarTitle: { fontSize: '20px', fontWeight: 700, color: '#0F172A', margin: 0 },
  
  scrollContent: { padding: '20px', paddingBottom: '40px', maxWidth: '700px', margin: '0 auto', width: '100%' },
  
  avatarSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px', marginBottom: '32px' },
  avatarContainer: { position: 'relative', marginBottom: '16px' },
  avatarPlaceholder: { width: '100px', height: '100px', borderRadius: '50px', backgroundColor: '#E2E8F0', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '4px solid #FFFFFF' },
  avatarImage: { width: '100px', height: '100px', borderRadius: '50px', border: '4px solid #FFFFFF', objectFit: 'cover' },
  avatarInitials: { fontSize: '32px', fontWeight: 700, color: '#64748B' },
  editAvatarBadge: { position: 'absolute', bottom: 0, right: 0, backgroundColor: '#0284C7', width: '32px', height: '32px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '3px solid #FFFFFF', boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', cursor: 'pointer', padding: 0 },
  
  userName: { fontSize: '24px', fontWeight: 800, color: '#0F172A', marginBottom: '6px', textAlign: 'center', margin: '0 0 6px 0' },
  roleContainer: { backgroundColor: '#F0F9FF', padding: '6px 12px', borderRadius: '16px', border: '1px solid #BAE6FD' },
  userRole: { fontSize: '13px', fontWeight: 700, color: '#0284C7', textTransform: 'uppercase', letterSpacing: '0.5px' },
  
  section: { marginBottom: '32px' },
  sectionHeader: { display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '8px' },
  sectionTitle: { fontSize: '18px', fontWeight: 700, color: '#0F172A', marginLeft: '8px', margin: '0 0 0 8px' },
  sectionSubtitle: { fontSize: '14px', color: '#64748B', marginBottom: '16px', lineHeight: '20px', margin: '0 0 16px 0' },
  
  card: { backgroundColor: '#FFFFFF', borderRadius: '20px', padding: '16px', boxShadow: '0px 4px 16px rgba(100, 116, 139, 0.08)', border: '1px solid #F1F5F9' },
  
  fieldRow: { padding: '14px 0', display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontSize: '12px', color: '#94A3B8', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  fieldValueContainer: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  fieldValue: { fontSize: '16px', color: '#1E293B', fontWeight: 600 },
  
  divider: { height: '1px', backgroundColor: '#F1F5F9', margin: '4px 0' },
  
  menuRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '16px 0', cursor: 'not-allowed', opacity: 0.8 },
  menuIconContainer: { width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '12px' },
  menuTextContainer: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  menuText: { fontSize: '15px', color: '#1E293B', fontWeight: 600, marginBottom: '2px' },
  menuSubText: { fontSize: '12px', color: '#94A3B8' },
  
  badgeSoon: { backgroundColor: '#F1F5F9', padding: '6px 10px', borderRadius: '12px' },
  badgeSoonText: { fontSize: '10px', color: '#64748B', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' },
};
